import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * Random position for each product, decided once per page load and reused by every re-render.
 * @type {Map<string, number>}
 */
const pageRandomRank = new Map();

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          const html = this.dataset.randomize === 'true' ? this.#randomize(result.data) : result.data;
          morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Shuffles the server-rendered product pool and keeps `data-random-limit` of them.
   * The order is decided once per page load (`pageRandomRank`), so re-renders keep it and
   * only a reload reshuffles, matching the collection page.
   * @param {string} html - Section HTML from the recommendations endpoint
   * @returns {string}
   */
  #randomize(html) {
    const limit = Number.parseInt(this.dataset.randomLimit ?? '', 10) || 8;
    const template = document.createElement('template');
    template.innerHTML = html;

    /** @param {Element} element */
    const productKey = (element) =>
      element.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('/products/')[1]?.split(/[?#]/)[0] ?? '';

    /** @param {Element[]} elements */
    const shuffled = (elements) => {
      for (const element of elements) {
        const key = productKey(element);
        if (!pageRandomRank.has(key)) pageRandomRank.set(key, Math.random());
      }
      return [...elements].sort(
        (a, b) => (pageRandomRank.get(productKey(a)) ?? 0) - (pageRandomRank.get(productKey(b)) ?? 0)
      );
    };

    // Carousels: only the outer carousel's slides, not the image slideshows inside each product card
    for (const track of template.content.querySelectorAll(
      '.resource-list__carousel > slideshow-container > slideshow-slides'
    )) {
      const slides = [...track.querySelectorAll(':scope > slideshow-slide')];
      const kept = shuffled(slides).slice(0, limit);
      for (const slide of slides) slide.remove();
      kept.forEach((slide, index) => {
        // Renumber so the carousel's per-slide scroll timelines and first-slide state stay correct
        if (slide instanceof HTMLElement) slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
        track.append(slide);
      });
    }

    // Grid layout
    for (const list of template.content.querySelectorAll('.resource-list--grid, [data-testid="resource-list-grid"]')) {
      const items = [...list.querySelectorAll(':scope > .resource-list__item')];
      const kept = shuffled(items).slice(0, limit);
      for (const item of items) item.remove();
      list.append(...kept);
    }

    return template.innerHTML;
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
